import {Injectable, inject} from '@angular/core';
import {NavigationError, Router} from '@angular/router';
import {filter} from 'rxjs';
import {ConfirmationPopupService} from '@services/confirmation-popup.service';

@Injectable({
  providedIn: 'root'
})
export class ChunkLoadErrorService {

  private router = inject(Router);
  private confirmationPopupService = inject(ConfirmationPopupService);
  private promptShown = false;

  constructor() {
    this.router.events
    .pipe(
      filter((event): event is NavigationError => event instanceof NavigationError),
    )
    .subscribe((event) => {
      if (this.isChunkLoadError(event.error)) {
        this.promptReload();
      }
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('error', this.handleWindowError);
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
  }

  private handleWindowError = (event: ErrorEvent): void => {
    if (this.isChunkLoadError(event.error || event.message)) {
      this.promptReload();
    }
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
    if (this.isChunkLoadError(event.reason)) {
      this.promptReload();
    }
  };

  private promptReload(): void {
    if (this.promptShown) {
      return;
    }
    this.promptShown = true;

    this.confirmationPopupService.confirm({
      title: 'Loading Issue',
      text: 'We had trouble loading some files. Reload the page and try again?',
      yesCaption: 'Yes',
      noCaption: 'No',
      yes: () => {
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      },
      no: () => {
        this.promptShown = false;
      }
    });
  }

  private isChunkLoadError(error: unknown): boolean {
    const normalized = this.getErrorText(error);
    if (!normalized) {
      return false;
    }

    return [
      'chunkloaderror',
      'loading chunk',
      'failed to fetch dynamically imported module',
      'importing a module script failed',
      'error loading dynamically imported module',
    ].some(pattern => normalized.includes(pattern));
  }

  private getErrorText(error: unknown): string {
    if (typeof error === 'string') {
      return error.toLowerCase();
    }

    if (error instanceof Error) {
      return `${error.name} ${error.message}`.toLowerCase();
    }

    if (error && typeof error === 'object') {
      const maybeError = error as {name?: unknown; message?: unknown};
      const name = typeof maybeError.name === 'string' ? maybeError.name : '';
      const message = typeof maybeError.message === 'string' ? maybeError.message : '';
      return `${name} ${message}`.toLowerCase().trim();
    }

    return '';
  }
}
